import styled from '@emotion/styled';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
    connectToAnExpertService,
    toastNotificationsService,
    userService,
} from 'src/config/rootService';
import { CareRecommendationRecommendation } from 'src/generated/webapp_gql';
import {
    getCategorySelections,
    getMostRecentCareRecommendation,
    getUserInformation,
} from 'src/modules/user/selectors/userSelectors';

import { Modal } from 'src/shared/components/Modal';
import { Button, Form, FormInputTextarea, pxToRem, useForm } from '@modernhealth/vital-components';

import { color, font, layout } from 'src/shared/styles/variables';

const StyledModal = styled(Modal)({
    '& .ant-modal-content': {
        maxWidth: layout.modalWidth.medium,
        margin: '0 auto',

        '& .ant-modal-body': {
            padding: '3.50rem 4.38rem 2.63rem',
        },
    },
});

const HeadingText = styled.h3(
    {
        color: color.pickledBluewood,
        textAlign: 'center',
    },
    font.heavy(font.size.header),
);

const DescriptionText = styled.p(
    {
        color: color.blueBayoux,
        textAlign: 'center',
    },
    font.medium(font.size.medium),
);

const FormWrapper = styled.div({
    maxWidth: 490,
    margin: `${pxToRem(16)} auto 0`,
});

interface Props {
    visible: boolean;
    helpDesk?: boolean;
    dataTestID?: string;
    header?: string;
    description?: string;
    onToggle: () => void;
    tags?: string[];
}

export const ContactUsModal: React.FC<Props> = ({
    onToggle,
    visible,
    helpDesk,
    header,
    dataTestID,
    description,
    tags = [],
}) => {
    const [submitting, setSubmitting] = useState(false);

    const { t } = useTranslation();

    const user = useSelector(getUserInformation);
    const careRecUuid = useSelector(getMostRecentCareRecommendation)?.uuid ?? null;
    const rec = useSelector(getMostRecentCareRecommendation)?.recommendation ?? undefined;
    const categories = useSelector(getCategorySelections);
    const phoneNumber = user?.phoneNumber ?? '';

    type ContactUsFormData = {
        anythingElse: string;
    };

    const formDefinition = useForm<ContactUsFormData>();

    const anythingElse = formDefinition.watch('anythingElse');

    useEffect(() => {
        if (!visible) {
            formDefinition.reset({ anythingElse: '' });
        }
    }, [visible]);

    const handleSubmit = async (data: ContactUsFormData) => {
        setSubmitting(true);

        const handleSuccess = () => {
            onToggle();
            toastNotificationsService.renderSuccessToast(
                t('shared:contact_us_modal.message_received'),
            );
        };

        if (helpDesk) {
            await connectToAnExpertService.submitForm(
                {
                    userInfo: user,
                    anythingElse: data.anythingElse,
                    phoneNumber,
                    therapistRequested: CareRecommendationRecommendation.Therapy === rec,
                    categories,
                    careRecUuid,
                    tags,
                },
                handleSuccess,
            );
        } else {
            try {
                await userService.submitHelpDeskRequest(data.anythingElse, [
                    t('shared:contact_us_modal.provide_personal_email'),
                ]);
                handleSuccess();
            } catch {
                // submitHelpDeskRequest will show generic error notification if promise rejected
            }
        }

        setSubmitting(false);
    };

    return (
        <React.Fragment>
            <StyledModal
                wrapProps={{
                    'data-testid': dataTestID,
                }}
                visible={visible}
                onCancel={onToggle}
                footer={null}
                width="auto"
                centered
            >
                <HeadingText>{header || t('shared:contact_us_modal.anything_else')}</HeadingText>
                <DescriptionText id="contact-us-label">
                    {description || t('shared:contact_us_modal.specific_requests_for_provider')}
                </DescriptionText>

                <FormWrapper>
                    <Form definition={formDefinition} onSubmit={handleSubmit}>
                        <FormInputTextarea
                            labelledBy="contact-us-label"
                            name="anythingElse"
                            placeholder={t('shared:contact_us_modal.share_more_here')}
                        />
                        <Button
                            busy={submitting}
                            disabled={!anythingElse}
                            fluid
                            type="submit"
                            variant="primary"
                        >
                            {t('shared:contact_us_modal.submit_request')}
                        </Button>
                    </Form>
                </FormWrapper>
            </StyledModal>
        </React.Fragment>
    );
};
